<template>
  <div class="container-content">
    <div class="header">
      <a class="header-back-link" title="Назад" @click="$router.push({ name: 'cabinet' })"></a>
      <div class="header-title" v-html="$t('work.like.self.employed')"></div>
    </div>
    <div class="fields">
      <div class="input-container">
        <input-text
          v-model="inn"
          v-mask="'############'"
          input-id="self-employed-inn"
          :input-name="$t('common.inn')"
          :input-placeholder="$t('enter.inn.number')"
          :min-length="12"
          :max-length="12"
          data-private
          required
        />
      </div>
      <div class="input-container">
        <input-text
          v-model="phone"
          v-mask="'+7##########'"
          input-id="self-employed-phone"
          :input-name="$t('phone.number.only.rf')"
          input-placeholder="+71234567890"
          :min-length="12"
          :max-length="12"
          data-private
          required
        />
      </div>
      <p v-html="$t('self.employed.privacy.policy', { getUserAgreementLinkBySelfEmployed })"></p>
    </div>
    <div class="actions">
      <div class="button-send">
        <button :disabled="isDisabled" @click="handlerOnRegistration">
          {{ $t('accept.status') }}
        </button>
      </div>
      <div class="text-information">
        <p v-html="$t('get.self.employed.status.on.fns.site.or.app')"></p>
        <p>
          <a href="https://help.capusta.space/samozanyatoy_npd" target="_blank">{{ $t('common.details') }}</a>
        </p>
      </div>
      <div class="button-link-container">
        <a href="https://npd.nalog.ru/web-app/" target="_blank">
          <div class="button-link">{{ $t('fns.site') }}</div>
        </a>
        <a href="https://play.google.com/store/apps/details?id=com.gnivts.selfemployed&hl=ru" target="_blank">
          <div class="button-link">{{ $t('for.android') }}</div>
        </a>
        <a href="https://apps.apple.com/ru/app/%D0%BC%D0%BE%D0%B9-%D0%BD%D0%B0%D0%BB%D0%BE%D0%B3/id1437518854?l=en" target="_blank">
          <div class="button-link">{{ $t('for.iphone') }}</div>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import { mask } from 'vue-the-mask';

import HTTPService from '@/services/Http';

import InputText from '@/components/inputElements/InputText';

const INN_REG_EXP = /^\d{12}$/;
const PHONE_REG_EXP = /^\+?[0-9]{11}$/g;

export default {
  name: 'RegistrationSelfEmployed',
  directives: {
    mask,
  },
  components: {
    InputText,
  },
  created() {
    this.checkNotification();
  },
  data() {
    return {
      inn: '',
      phone: '',
      loading: false,
    };
  },
  computed: {
    isValidInn() {
      return INN_REG_EXP.test(this.inn);
    },
    isValidPhone() {
      return PHONE_REG_EXP.test(this.phone);
    },
    isValid() {
      return this.isValidInn && this.isValidPhone;
    },
    isDisabled() {
      return this.loading || !this.isValid;
    },
    requestData() {
      return {
        inn: this.inn,
        phone: this.phone,
      };
    },
    getUserAgreementLinkBySelfEmployed() {
      return 'https://docs.capusta.space/self-employer.pdf';
    },
    ...mapState(['SELF_EMPLOYED']),
  },
  methods: {
    async handlerOnRegistration() {
      if (!this.isValid) {
        return;
      }

      this.loading = true;

      try {
        const { data: taxService } = await this.GET_SELF_EMPLOYED_TAX_SERVICE_STATUS();

        if (taxService.status !== 'AVAILABLE') {
          this.loading = false;

          this.$alert({
            type: 'warning',
            text: this.$t('fns.is.not.responding'),
          });
          return;
        }
      } catch (error) {
        this.loading = false;

        this.$alert({
          type: 'warning',
          text: this.$t('fns.is.not.responding'),
        });
        return;
      }

      HTTPService.selfEmployedRegistration(this.requestData)
        .then(response => {
          this.SET_SELF_EMPLOYED_ID(response.data.requestId);
          this.SET_SELF_EMPLOYED_STATUS('REQUEST_PENDING');
        })
        .catch(() => {
          this.$alert({
            type: 'warning',
            text: this.$t('inn.not.exists'),
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    checkNotification() {
      if (this.SELF_EMPLOYED.status !== 'FAILED') {
        return;
      }

      this.$alert({
        type: 'warning',
        text: this.$t('connection.denied'),
      });
    },
    ...mapActions(['GET_SELF_EMPLOYED_TAX_SERVICE_STATUS']),
    ...mapMutations(['SET_SELF_EMPLOYED_STATUS', 'SET_SELF_EMPLOYED_ID']),
  },
};
</script>

<style lang="sass" scoped>
  .container-content
    width: 100%
    padding: 20px 0
    position: relative

    +respond-to("small")
      width: 500px
      margin: 0 auto
      padding: 60px 0

  .header
    display: flex
    margin-bottom: 12px
    padding: 0 10px

    +respond-to("small")
      margin-bottom: 30px

  .header-back-link
    width: 40px
    height: 40px
    margin-right: 7px
    border-radius: 50%
    background-image: url("~@/assets/img/arrow-left.svg")
    background-repeat: no-repeat
    background-position: center

    #{theme("eclipse")} &
      background-color: #cbe2b3

    #{theme("obsidian")} &
      background-color: #181722

    +respond-to("small-medium")
      margin-left: -60px

    +respond-to("small")
      width: 60px
      height: 60px
      margin-right: 20px

    &:hover
      background-color: #7CB342

  .header-title
    align-self: center
    font-size: 20px
    line-height: 24px

    &:deep span
      display: block

      +respond-to("small")
        display: inline

  .fields
    position: relative
    padding: 20px
    margin-bottom: 40px

    +respond-to("small")
      border-radius: 10px

    #{theme("eclipse")} &
      background-color: $pure-white

    #{theme("obsidian")} &
      background-color: #181822

    &:after
      +pseudo-block

      bottom: -1.88rem
      left: calc(50% - 2.48rem)
      border-width: 2.24rem 2.24rem 0 2.24rem
      border-style: solid

      #{theme("eclipse")} &
        border-color: $pure-white transparent transparent

      #{theme("obsidian")} &
        border-color: #181822 transparent transparent

    p
      font-weight: 500
      font-size: 14px
      line-height: 20px
      color: #fff
      opacity: .5

      #{theme("eclipse")} &
        color: #212121

      a
        text-decoration: underline

  .input-container
    margin-bottom: 20px

  .actions
    width: 100%
    margin: 0 auto
    padding: 0 20px

  .button-send
    margin-bottom: 30px

    +respond-to("small")
      margin-bottom: 40px

    button
      width: 100%
      display: block
      margin: 0 auto
      padding: 16px
      font-weight: 600
      font-size: 18px
      line-height: 18px
      border-radius: 2rem
      background-color: #7CB342
      box-shadow: inset 0px -1px 0px rgba(0, 0, 0, 0.25)

      +respond-to("small")
        width: 300px

      &:hover(:not):disabled
        box-shadow: inset 0 -0.24rem 0 rgba(0, 0, 0, 0.2), 0 0.24rem 0.24rem rgba(124, 179, 66, 0.2)

      &:disabled,
      &.disabled
        color: #6b6b76
        background-color: #3a3a49
        cursor: not-allowed

  .text-information
    width: 100%
    max-width: 300px
    margin: 0 auto
    padding-bottom: 20px
    font-weight: 500
    font-size: 16px
    line-height: 20px
    text-align: center
    color: #8e8e98

    a
      color: #7CB342
      text-decoration: underline

      &:hover
        text-decoration: none

    +respond-to("small")
      max-width: 100%
      padding-bottom: 30px

      &:deep span
        display: inline-block

  .button-link-container
    width: 280px
    margin: 0 auto
    display: flex
    flex-flow: row wrap
    justify-content: space-between

    a:last-child
      width: 100%

    a:not(:last-child)
      margin-bottom: 20px

    +respond-to("small")
      width: 100%
      justify-content: space-between

      a:last-child
        width: auto

      a:not(:last-child)
        margin-bottom: 0px

  .button-link
    width: 100%
    min-width: 130px
    max-width: 140px
    margin: 0 auto
    padding: 10px 20px
    text-align: center
    border-radius: 50px
    font-weight: 500
    font-size: 14px
    line-height: 20px
    color: #fff
    background: #27283C
    box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.15)

    +respond-to("small")
      width: 140px
</style>
